/* eslint-disable no-template-curly-in-string */
import Balance from 'UIKit/Balance'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import CircleLoader from 'UIKit/Loader/CircleLoader'
import { ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text/Text'
import TokenLogo from 'UIKit/TokenLogo'
import BigNumber from 'bignumber.js'
import { HUSD_TOKEN } from 'config/constants/tokens'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import Column from 'layout/Components/Column'
import { RowBetween, RowMiddle } from 'layout/Components/Row'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import colors from 'theme/ui/colors'
import { getFullDisplayBalance } from 'utils/formatBalance'
import InstructionAirdropModal from './HusdAirdrop/InstructionHUSDAirdropModal'
import { useHusdBonus } from './hooks'
import {
  ModalWrapper,
  ScaleVariantBonus,
  StyledArrowButton,
  StyledButton,
  StyledTag,
  StyledTitle,
  Wrapper,
  WrapperContentDropdown,
} from './styled'

const HUSDBonusContent: React.FC<{
  balance: BigNumber
  claimableBalance: BigNumber
  disabled: boolean
  isSubmiting: boolean
  onClaim: () => void
  onPresentHUSDModal: () => void
  onDismiss?: () => void
}> = ({ balance, claimableBalance, disabled, isSubmiting, onClaim, onPresentHUSDModal, onDismiss = () => {} }) => {
  return (
    <Column>
      <Column>
        <StyledTitle mb="6px">
          <Trans>Unlockable Amount</Trans>
        </StyledTitle>
        <Text bold>${getFullDisplayBalance(balance, 0, 6)}</Text>
      </Column>
      <Column my={['20px', '', '12px']}>
        <StyledTitle mb="6px">
          <Trans>Unlock Amount</Trans> (<Trans>Claimable</Trans>)
        </StyledTitle>
        <Text bold color="success">
          ${getFullDisplayBalance(claimableBalance, 0, 6)}
        </Text>
      </Column>
      <Column mt={['20px', '', '0']}>
        <StyledButton
          fontSize={['14px', '', '12px']}
          primary
          mb="8px"
          maxHeight={['', '', '28px']}
          onClick={() => {
            onClaim()
            onDismiss()
          }}
          disabled={disabled}
        >
          {isSubmiting && (
            <Box mr="8px">
              <CircleLoader scale={0.8} />
            </Box>
          )}

          <Trans>Claim now</Trans>
        </StyledButton>

        <StyledButton
          fontSize={['14px', '', '12px']}
          style={{
            color: theme.colors.textTertiary,
          }}
          onClick={onPresentHUSDModal}
        >
          <Trans>View Details</Trans>
        </StyledButton>
      </Column>
    </Column>
  )
}
export const HUSDBonusDropdown: React.FC<{
  balance: BigNumber
  claimableBalance: BigNumber
  disabled: boolean
  isSubmiting: boolean
  onClaim: () => void
  onPresentHUSDModal: () => void
}> = ({ balance, claimableBalance, disabled, isSubmiting, onClaim, onPresentHUSDModal }) => {
  return (
    <WrapperContentDropdown>
      <HUSDBonusContent
        balance={balance}
        claimableBalance={claimableBalance}
        disabled={disabled}
        isSubmiting={isSubmiting}
        onClaim={onClaim}
        onPresentHUSDModal={onPresentHUSDModal}
      />
    </WrapperContentDropdown>
  )
}

export const HusdBonusModal: React.FC<ModalProps> = ({ onDismiss = () => {}, ...props }) => {
  const { unlockableBalance, claimableBalance, handleClaimHUSD, isDisabled, onPresentHUSDModal, isSubmiting } =
    useHusdBonus()
  return (
    <StyledModalWrapper title="HUSD Bonus" onDismiss={onDismiss} className="modal-wrapper" {...props}>
      <HUSDBonusContent
        balance={unlockableBalance.minus(claimableBalance)}
        claimableBalance={claimableBalance}
        onClaim={handleClaimHUSD}
        onPresentHUSDModal={onPresentHUSDModal}
        disabled={isDisabled}
        onDismiss={onDismiss}
        isSubmiting={isSubmiting}
      />
    </StyledModalWrapper>
  )
}

const StyledModalWrapper = styled(ModalWrapper)`
  &.modal-wrapper {
    ${({ theme }) => theme.mediaQueries.sm} {
      min-width: 380px !important;
      max-width: 380px !important;
      width: 380px !important;
    }
  }
`

const HUSDBonus: React.FC<ScaleVariantBonus & BoxProps> = ({ tagButtonEnable, ...props }) => {
  const { t } = useTranslation()
  const { unlockableBalance, claimableBalance, handleClaimHUSD, isDisabled, onPresentHUSDModal, isSubmiting } =
    useHusdBonus()
  const { isDesktop } = useMatchBreakpoints()
  const [onPresent] = useModal(HusdBonusModal)
  const [openInstructionAirdropModal] = useModal(InstructionAirdropModal)

  return (
    <Wrapper
      dropdownPosition="bottom-left"
      dropdownContent={
        isDesktop && (
          <HUSDBonusDropdown
            balance={unlockableBalance.minus(claimableBalance)}
            claimableBalance={claimableBalance}
            onClaim={handleClaimHUSD}
            onPresentHUSDModal={onPresentHUSDModal}
            disabled={isDisabled}
            isSubmiting={isSubmiting}
          />
        )
      }
      title={t('HUSD Rakeback')}
      onClick={() => {
        if (!isDesktop) {
          onPresent()
        }
      }}
      {...props}
    >
      <RowBetween>
        <Flex width="100%" alignItems="center" justifyContent={['flex-start', '', 'space-between']}>
          <RowMiddle>
            <Box width="20px" id="airdrop-husd-logo" position="relative">
              <TokenLogo token={HUSD_TOKEN} size={20} />

              <NotificationHUSDAirdropContainer
                position="absolute"
                top="150%"
                left="-300%"
                width="fit-content"
                id="notification-husdbonus-container"
                onClick={() => {
                  openInstructionAirdropModal()
                }}
                onMouseOver={(e) => {
                  e.stopPropagation()
                }}
              >
                <HUSDAirdropBox>
                  <Text fontSize="12px" id="notification-husdbonus" />
                  <Box ml={2} height="16px">
                    <Icons.InfoCircleIcon width="16px" fill={colors.textDisable} />
                  </Box>
                </HUSDAirdropBox>
              </NotificationHUSDAirdropContainer>
            </Box>

            <Text fontSize={['14px', '', '12px']} bold display="flex" ml="4px">
              $<Balance value={Number(unlockableBalance)} decimals={2} fontSize={['14px', '', '12px']} bold />
            </Text>
          </RowMiddle>

          {claimableBalance.gt(0) && (
            <StyledTag
              style={{
                whiteSpace: 'nowrap',
              }}
              display={tagButtonEnable ? 'flex !important' : 'none !important'}
              ml="6px"
            >
              {t('Claim')}
            </StyledTag>
          )}
        </Flex>
        <StyledArrowButton ml="8px" disabled />
      </RowBetween>
    </Wrapper>
  )
}

const NotificationHUSDAirdropContainer = styled(Box)`
  z-index: 1;
  display: none;

  &::after {
    content: '';
    position: absolute;
    top: -12%;
    left: 65px;
    width: 10px;
    height: 10px;
    transform: rotate(135deg);
    border-radius: 1px;
    background: #22262e;
    z-index: -2;
  }

  &.appear-animation {
    @keyframes appearAnimation {
      0% {
        opacity: 0;
        transform: scale(0);
      }
      50% {
        opacity: 0.5;
        transform: scale(0.5);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    animation: appearAnimation 0.3s ease-in-out;
  }
`

const HUSDAirdropBox = styled(Box)`
  width: max-content;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid #1d2533;
  background: #22262e;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  position: sticky;
`

export default HUSDBonus
